import {
  ContextMenuDropdown,
  SkeletonLoader,
  AnchorButton,
  H2,
  Text,
} from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';

import projectKeys from '@/api/queries/project/keyFactory';
import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import DealTemplateModal, {
  DealTemplateModalViews,
} from '@/ui/components/dealTemplateModal/DealTemplateModal';
import EntityTagControl from '@/ui/components/entityTagControl/EntityTagControl';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import DeleteProjectModal from '@/ui/pages/projectPage/components/ProjectHeader/DeleteProjectModal';
import EditProjectModal from '@/ui/pages/projectPage/components/ProjectHeader/EditProjectModal';
import PipelineControl from '@/ui/pages/projectPage/components/ProjectHeader/PipelineControl';
import PipelineStageControl from '@/ui/pages/projectPage/components/ProjectHeader/PipelineStageControl';
import ProjectOwnerControl from '@/ui/pages/projectPage/components/ProjectHeader/ProjectOwnerControl';
import RestoreProjectModal from '@/ui/pages/projectPage/components/ProjectHeader/RestoreProjectModal';

import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import * as dealTemplateActions from '@payaca/store/dealTemplates/dealTemplateActions';
import { TaggableEntityType } from '@payaca/types/tagTypes';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../api/queries/project/useGetProjectByIdOverview';

const ProjectHeader: FC = () => {
  const dispatch = useDispatch();
  const { userHasRequiredPermission } = usePermissions();

  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const { project, isLoading: isProjectDataLoading } =
    useGetProjectByIdOverview(projectId);

  // *start*
  // ToDo: remove use of redux in this component
  useEffect(() => {
    // fetch deal templates with empty search to see if any exist on account
    dispatch(dealTemplateActions.requestGetDealTemplates(''));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);
  const dealTemplates = useSelector(
    (state: any) => state.dealTemplates.dealTemplates
  );
  // *end*

  const [showDealTemplateModal, setShowDealTemplateModal] = useState<
    DealTemplateModalViews | undefined
  >();
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] =
    useState(false);
  const [isRestoreProjectModalOpen, setIsRestoreProjectModalOpen] =
    useState(false);

  const projectContextMenuOptions = useMemo(() => {
    const options = [];
    if (userHasRequiredPermission([DealsPermissions.CREATE_TEMPLATE])) {
      options.push({
        label: 'Create template',
        onClick: () => {
          setShowDealTemplateModal(DealTemplateModalViews.CREATE);
        },
      });
    }

    // Legacy (v1) Projects can no longer be restored, so omit the action.
    if (project?.archivedAt && project.version >= 2) {
      options.push({
        label: 'Restore project',
        onClick: () => {
          setIsRestoreProjectModalOpen(true);
        },
      });
    } else if (!project?.archivedAt) {
      if (
        userHasRequiredPermission([
          DealsPermissions.UPDATE_DEAL_DESCRIPTION,
          DealsPermissions.UPDATE_DEAL_REFERENCE,
        ])
      ) {
        options.push({
          label: 'Edit reference/description',
          onClick: () => setIsEditProjectModalOpen(true),
        });
      }

      if (
        userHasRequiredPermission([DealsPermissions.APPLY_TEMPLATE]) &&
        dealTemplates.length
      ) {
        options.push({
          label: 'Load template',
          onClick: () => {
            setShowDealTemplateModal(DealTemplateModalViews.LOAD);
          },
        });
      }

      if (
        userHasRequiredPermission([
          DealsPermissions.ARCHIVE_DEAL,
          DealsPermissions.ARCHIVE_MY_DEAL,
        ])
      ) {
        options.push({
          label: 'Delete project',
          onClick: () => {
            setIsDeleteProjectModalOpen(true);
          },
        });
      }
    }

    return options;
  }, [project, dealTemplates, userHasRequiredPermission]);

  if (isProjectDataLoading) {
    return (
      <>
        <div className="mb-4">
          <SkeletonLoader.Title className="mb-4 w-1/4" />

          <div className="flex gap-4">
            <SkeletonLoader.Title />
            <SkeletonLoader.Title />
            <SkeletonLoader.Title />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:gap-4">
        <div className="order-1 sm:order-first">
          <H2>Project: #{project?.reference}</H2>
        </div>
        {project?.customer && (
          <AnchorButton
            as={Link}
            to={`/customers/${project.customer?.publicId}`}
            variant="link"
            className="order-first max-sm:self-start sm:order-1"
          >
            <H2>{project.customer.name}</H2>
          </AnchorButton>
        )}
        <div className="order-2 ml-auto">
          {project && (
            <EntityTagControl
              entityId={+project.id}
              entityType={TaggableEntityType.DEAL}
              entityTags={
                project?.tags.map((tag) => ({
                  id: +tag.id,
                  tagText: tag.name,
                  tagColour: tag.colour || '',
                  isSystemManaged: false,
                  archivedAt: null,
                })) || []
              }
              isReadOnly={project?.archivedAt !== null}
              onEntityTagsChange={() => {
                void queryClient.invalidateQueries({
                  queryKey: projectKeys.overview(projectId),
                });
              }}
            />
          )}
        </div>
      </div>

      {project?.description && <Text>{project.description}</Text>}

      <div className="my-4 flex items-start gap-4 lg:items-stretch">
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          {project?.type === 'STANDARD' && (
            <>
              <PermissionGuard
                renderIfHasPermissions={[
                  DealsPermissions.UPDATE_DEAL,
                  DealsPermissions.UPDATE_MY_DEAL,
                ]}
              >
                <div className="min-w-0">
                  <PipelineControl />
                </div>
                <div className="min-w-0">
                  <PipelineStageControl />
                </div>
              </PermissionGuard>

              <PermissionGuard
                renderIfHasPermissions={[
                  JobPermissions.UPDATE_JOB,
                  DealsPermissions.GET_DEAL,
                ]}
                requireAllPermissions={true}
              >
                <div className="min-w-0">
                  <ProjectOwnerControl />
                </div>
              </PermissionGuard>
            </>
          )}
        </div>
        {!!projectContextMenuOptions.length && (
          <ContextMenuDropdown
            variant={'secondary'}
            size={'sm'}
            menuItems={projectContextMenuOptions}
          />
        )}
      </div>

      {project && showDealTemplateModal && (
        <DealTemplateModal
          dealId={+project.id}
          projectPublicId={project.publicId}
          isOpen={!!showDealTemplateModal}
          view={showDealTemplateModal}
          onClose={() => setShowDealTemplateModal(undefined)}
        />
      )}

      <EditProjectModal
        isOpen={isEditProjectModalOpen}
        onClose={() => setIsEditProjectModalOpen(false)}
      />

      <DeleteProjectModal
        isOpen={isDeleteProjectModalOpen}
        onClose={() => setIsDeleteProjectModalOpen(false)}
      />

      <RestoreProjectModal
        isOpen={isRestoreProjectModalOpen}
        onClose={() => setIsRestoreProjectModalOpen(false)}
      />
    </>
  );
};

export default ProjectHeader;
